import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isLoaded } from 'react-redux-firebase';
import { useNavigate, useParams } from 'react-router-dom';
import { BackgroundVideo } from '../../components/BackgroundVideo';
import { Loading } from '../../components/Loading';
import { Modal } from '../../components/Modal';
import { useAuth } from '../../hooks/useAuth';
import { useCountryMarketHelpers } from '../../hooks/useCountryMarketHelpers';
import { useCustomerState } from '../Briefing/CustomerState';
import CountryMarketSelector from '../CountryMarketSelector/CountryMarketSelector';
import { CustomerUserSelection } from '../CustomerUserSelection';

export const ChooseCountryMarket: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { redirectTargetByCountryMarket } = useCountryMarketHelpers();

  const { reason } = useParams<{ reason: string }>();

  const [{ user }] = useAuth();
  const userIsAzelisEmployee = useMemo(
    () => isLoaded(user) && user.azelis_employee,
    [user],
  );

  const [{ cpCustomer, isSelectionConfirmed }] = useCustomerState();

  useEffect(() => {
    if (!userIsAzelisEmployee || !isSelectionConfirmed || !cpCustomer) return;
    navigate(
      redirectTargetByCountryMarket(cpCustomer.marketCountryName, 'start'),
    );
  }, [
    cpCustomer,
    isSelectionConfirmed,
    navigate,
    redirectTargetByCountryMarket,
    userIsAzelisEmployee,
  ]);

  const loadingContent = useMemo(
    () => (
      <Modal
        heading=""
        isHidden={false}
        cancelText=""
        isComplex={true}
        confirmText={t('components:countryMarketSelector:confirm')}
        isConfirmButtonDisabled={true}
        isCancelHidden={true}
      >
        <Loading />
      </Modal>
    ),
    [t],
  );

  const content = useMemo(() => {
    switch (true) {
      case !isLoaded(user):
        return loadingContent;
      case userIsAzelisEmployee:
        // tracking of employee user can only take place after a customer user has been selected
        return <CustomerUserSelection />;
      default:
        return <CountryMarketSelector reason={reason} />;
    }
  }, [loadingContent, reason, user, userIsAzelisEmployee]);

  return (
    <>
      <BackgroundVideo name={'intro'} fullscreen />
      {content}
    </>
  );
};
